import { useEffect, useRef, useState } from 'react';
import { config } from '../config.js';
import { detectBrowserAndDevice } from '../lib/device.js';
import { isNotFound, resolveQrTarget } from '../lib/resolveQr.js';

/**
 * Resolves a QR code and navigates to its target.
 *
 * Returns { status, error } where status is 'loading' | 'not-found' | 'error'.
 * There is no 'done' state: success leaves the page entirely.
 */
export function useQrRedirect(qrCodeId) {
  const [state, setState] = useState({ status: 'loading', error: null });

  // StrictMode mounts effects twice in development. Without this the API
  // would be hit twice per scan and the analytics doubled. The request is
  // deliberately not aborted on cleanup - the second mount would find the
  // guard set and never re-issue it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let device = { deviceid: 'unknown', devicename: 'Unknown Device', domain: '' };
    try {
      device = detectBrowserAndDevice();
    } catch {
      // Fingerprinting is best effort and must never block the redirect.
    }

    resolveQrTarget({
      qrCodeId,
      deviceId: device.deviceid,
      deviceName: device.devicename,
      domain: device.domain,
    })
      .then((url) => {
        // replace() so Back returns the visitor to whatever they were looking
        // at before scanning, rather than bouncing through this page again.
        window.location.replace(url);
      })
      .catch((err) => {
        if (isNotFound(err.code)) {
          setState({ status: 'not-found', error: err });
          return;
        }
        if (config.fallbackUrl) {
          window.location.replace(config.fallbackUrl);
          return;
        }
        setState({ status: 'error', error: err });
      });
  }, [qrCodeId]);

  return state;
}
